import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import ReactPixel from 'react-facebook-pixel';
import { useTranslation } from 'react-i18next';

import { GA_ID, PIXEL_ID } from 'shared/constants';

import { connect } from 'react-redux';
import {
  registrateUser,
  retrieveToken,
  clearRegistrateUser,
  toggleLivechat,
  clearResendEmailConfirmation,
  validateUsername,
  clearValidateUsername,
} from 'redux/users/actions';
import { PUBLIC_SITE_URL } from 'utils/restapi';

import { Link } from 'react-router-dom';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { Form } from 'semantic-ui-react';

import InputTextField from 'components/redux-form-adapters/InputTextField';
import Button from 'components/elements/buttons/Button';
import CommonMessage from 'components/common/CommonMessage';
import RegistrationSuccess from 'components/authentication/RegistrationSuccess';
import GoogleButton from 'components/elements/buttons/AuthenticationButton/google';
import FacebookButton from "components/elements/buttons/AuthenticationButton/facebook";

import validate from './validate';

import './SignupForm.css';

const SignupForm = props => {
  const {
    registrateUser,
    retrieveToken,
    clearRegistrateUser,
    clearResendEmailConfirmation,
    toggleLivechat,
    registrateUserSuccess,
    registrateUserError,
    handleSubmit,
    change,
    valid,
    email,
    username,
    password,
    validateUsername,
    clearValidateUsername,
    validateUsernameSuccess,
    validateUsernameError,
  } = props;

  const { t } = useTranslation();

  useEffect(() => {
    if (validateUsernameSuccess && validateUsernameSuccess.valid) {
      registrateUser(email, username, password);
      clearValidateUsername();
    }
  }, [validateUsernameSuccess]);

  useEffect(() => {
    if (registrateUserSuccess) {
      retrieveToken(email.toLowerCase(), password);
    }
  }, [registrateUserSuccess]);

  useEffect(() => {
    toggleLivechat(false);
    ReactGA.initialize(GA_ID);
    ReactGA.pageview(window.location.pathname + window.location.search);

    const pixelOptions = {
      autoConfig: true,
      debug: false,
    };
    ReactPixel.init(PIXEL_ID, null, pixelOptions);
    ReactPixel.pageView();

    return () => {
      clearRegistrateUser();
      clearResendEmailConfirmation();
      toggleLivechat(true);
    };
  }, []);

  const _handleSubmit = values => {
    validateUsername(values.username, values.email);
  };

  const renderFields = () => (
    <>
      {/* Email Address */}
      <Field
        label={t("Email Address")}
        component={InputTextField}
        name="email"
        type="email"
        required
      />
      {/* Username */}
      <Field
        label={t("Username")}
        component={InputTextField}
        name="username"
        type="text"
        prefix="heylink.me/"
        required
      />
      {/* Password 1 */}
      <Field
        label={t("Password")}
        component={InputTextField}
        name="password1"
        type="password"
        required
      />
      {/* Password 2 */}
      <Field
        label={t("Confirm Password")}
        component={InputTextField}
        name="password2"
        type="password"
        required
      />
    </>
  );

  return (
    <div className="signup-form__component">
      <div className="signup-form__component--wrapper">
        {!registrateUserSuccess &&
          <h1>{t("Get started for free")}</h1>
        }

        {validateUsernameSuccess &&
          !validateUsernameSuccess.valid &&
          validateUsernameSuccess.suggested_usernames &&
          validateUsernameSuccess.suggested_usernames.length > 0 &&
          <div className="suggested-usernames">
            <CommonMessage warning>
              <div className="text">
                <div>That username is taken. Try another.</div>
                <div>Available:</div>
              </div>
              {validateUsernameSuccess.suggested_usernames.map(item => (
                <div
                  className="username"
                  onClick={() => change('username', item)}
                  role="button"
                  tabIndex={-1}
                >
                  {item}
                </div>
              ))}
            </CommonMessage>
          </div>
        }

        {!registrateUserSuccess &&
          <Form
            onSubmit={handleSubmit(_handleSubmit)}
            error={!valid}
          >

            {renderFields()}

            {validateUsernameSuccess &&
              !validateUsernameSuccess.valid &&
              !validateUsernameSuccess.suggested_usernames &&
              validateUsernameSuccess.error &&
              <CommonMessage
                error
                errors={[
                  'Sorry, the username you entered is not a valid username. Your username is a part of your HeyLink.me URL e.g. heylink.me/username'
                ]}
              />
            }

            {registrateUserError &&
              <CommonMessage
                error
                errors={registrateUserError}
              />
            }

            <Button submit>{t("Sign up")}</Button>
            <span className="auth-label-text">{t("Or sign up with your Google email")}</span>
            <GoogleButton verb={t("Sign up")} />
            <FacebookButton verb={t("Sign up")} />

            <div className="agrement-section">
              <span>{t("Already have an account?")} </span>
              <Link to="/login">{t("Log In")}</Link>
            </div>

          </Form>
        }

        {registrateUserSuccess &&
          <RegistrationSuccess
            email={email}
          />
        }

      </div>

      <div className="agrement-section outside">
        <span>{t("By signing up you agree to HeyLink.me")} </span>
        <a href={`${PUBLIC_SITE_URL}/terms`}>{t("Terms")}</a> & <a href={`${PUBLIC_SITE_URL}/privacy`}>{t("Policies")}</a>
      </div>

    </div>
  );
};

SignupForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  registrateUser: PropTypes.func.isRequired,
  clearRegistrateUser: PropTypes.func.isRequired,
  clearResendEmailConfirmation: PropTypes.func.isRequired,
  toggleLivechat: PropTypes.func.isRequired,
  validateUsername: PropTypes.func.isRequired,
  clearValidateUsername: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  registrateUserSuccess: PropTypes.object,
  registrateUserError: PropTypes.object,
  valid: PropTypes.bool,
  email: PropTypes.string,
  username: PropTypes.string,
  password: PropTypes.string,
  validateUsernameSuccess: PropTypes.object,
  validateUsernameError: PropTypes.object,
};

SignupForm.defaultProps = {
  registrateUserSuccess: null,
  registrateUserError: null,
  valid: null,
  email: null,
  username: null,
  password: null,
  validateUsernameSuccess: null,
  validateUsernameError: null,
};

const mapStateToProps = (state) => {
  const selector = formValueSelector('SignupForm');
  return {
    email: selector(state, 'email'),
    username: selector(state, 'username'),
    password: selector(state, 'password1'),
    registrateUserSuccess: state.usersReducer.registrateUserSuccess,
    registrateUserError: state.usersReducer.registrateUserError,
    validateUsernameSuccess: state.usersReducer.validateUsernameSuccess,
    validateUsernameError: state.usersReducer.validateUsernameError,
  };
};

export default connect(
  mapStateToProps,
  {
    registrateUser,
    retrieveToken,
    clearRegistrateUser,
    toggleLivechat,
    clearResendEmailConfirmation,
    validateUsername,
    clearValidateUsername,
  },
)(reduxForm({
  form: 'SignupForm',
  validate,
})(SignupForm));
